// RF-DEL-02..05: project settings danger zone. Deleting needs the project's name typed exactly; the API enforces the
// role (organization admin or project owner) and refuses while work is running. The GitHub repository is kept.
import { useState } from "react";
import { useNavigate } from "react-router";
import { useI18n } from "../../i18n/index.tsx";
import { deleteProject } from "../../api/endpoints.ts";
import { Button } from "../../components/Button.tsx";
import { TextField } from "../../components/Field.tsx";
import { Banner, localErrorMessage } from "../../components/Feedback.tsx";

/** RF-DEL-02: the typed confirmation matches the project name exactly (surrounding whitespace ignored). */
export function deletionNameMatches(typed: string, projectName: string): boolean {
  return typed.trim().length > 0 && typed.trim() === projectName.trim();
}

export function DeleteProjectSection({ projectId, projectName }: { projectId: string; projectName: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const matches = deletionNameMatches(typed, projectName);

  const confirm = async () => {
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      await deleteProject(projectId);
      navigate("/");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card--pad stack" aria-labelledby="danger-zone-title" data-testid="project-danger-zone">
      <h2 id="danger-zone-title" className="card-title">
        {t("projectDelete.dangerZone")}
      </h2>
      <p className="muted">{t("projectDelete.body")}</p>
      <p className="muted">{t("projectDelete.repositoryKept")}</p>
      {!open ? (
        <div>
          <Button variant="danger" onClick={() => setOpen(true)}>
            {t("projectDelete.button")}
          </Button>
        </div>
      ) : (
        <div className="stack">
          <TextField
            label={t("projectDelete.typeName", { name: projectName })}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            data-testid="project-delete-name"
          />
          {error ? <Banner tone="error">{localErrorMessage(error, t)}</Banner> : null}
          <div className="dialog-footer">
            <Button
              onClick={() => {
                setOpen(false);
                setTyped("");
                setError(null);
              }}
              disabled={busy}
            >
              {t("common.cancel")}
            </Button>
            <Button variant="danger" loading={busy} disabled={!matches} onClick={() => void confirm()} data-testid="project-delete-confirm">
              {t("projectDelete.confirm")}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
